import type { DashboardResponse, GeographicScope } from "@rbim/shared";
import { Home, RefreshCw, ShieldCheck, UsersRound } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiRequestError, apiRequest } from "../api";
import { useAuth } from "../auth";
import { MunicipalityBarangayMap } from "../components/municipality-barangay-map";
import {
  reconcileDashboardBarangaySelection,
  resolveDashboardPrimaryTotals,
} from "../features/dashboard/dashboard-map-selection";
import { DashboardStatCard } from "../features/dashboard/dashboard-stat-card";
import { RecentActivityTable } from "../features/dashboard/recent-activity-table";
import { LiraWidget } from "../features/lira/lira-chat";

const emptyBarangayStatistics: DashboardResponse["barangays"] = [];
const emptyRecentActivity: DashboardResponse["recentActivity"] = [];

export function DashboardPage() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const requestSequence = useRef(0);
  const requestInFlight = useRef(false);

  const loadDashboard = useCallback(() => {
    // Dashboard data is intentionally request-driven instead of timer-driven.
    // Do not start another request while the current one is still in flight.
    if (requestInFlight.current) return;

    requestInFlight.current = true;
    const requestId = ++requestSequence.current;
    setLoading(true);
    setError("");

    apiRequest<DashboardResponse>("/dashboard")
      .then((response) => {
        if (requestSequence.current !== requestId) return;
        setDashboard(response);
        setError("");
      })
      .catch((requestError: unknown) => {
        if (requestSequence.current !== requestId) return;
        setError(
          requestError instanceof ApiRequestError
            ? requestError.message
            : "Unable to load dashboard statistics.",
        );
      })
      .finally(() => {
        // A stale request must never clear the in-flight guard for a newer one.
        if (requestSequence.current !== requestId) return;
        requestInFlight.current = false;
        setLoading(false);
      });
  }, []);

  // Load once when the dashboard is opened. Further server reads are explicit
  // user refreshes; focus, reconnect, and timers must not trigger API polling.
  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => () => {
    requestSequence.current += 1;
    requestInFlight.current = false;
  }, []);

  if (!user) return null;

  const activityPath = user.permissions.includes("audit.view")
    ? "/audit"
    : user.permissions.includes("sync.monitor")
      ? "/synchronization"
      : null;

  return (
    <DashboardView
      dashboard={dashboard}
      fallbackLocationLabel={user.locationLabel}
      fallbackScope={user.scope}
      loading={loading}
      error={error}
      onRetry={loadDashboard}
      onRefresh={loadDashboard}
      activityPath={activityPath}
      showRecentActivity={user.role !== "MAYOR_VIEWER"}
      showLira={user.role !== "BARANGAY_ENCODER" && user.permissions.includes("ai.query")}
    />
  );
}

export function DashboardView({
  dashboard,
  fallbackLocationLabel,
  fallbackScope,
  loading,
  error = "",
  onRetry,
  onRefresh,
  activityPath = null,
  showRecentActivity = true,
  showLira = false,
}: {
  dashboard: DashboardResponse | null;
  fallbackLocationLabel: string;
  fallbackScope: GeographicScope;
  loading: boolean;
  error?: string;
  onRetry?: () => void;
  onRefresh?: () => void;
  activityPath?: string | null;
  showRecentActivity?: boolean;
  showLira?: boolean;
}) {
  const locationLabel = dashboard?.locationLabel ?? fallbackLocationLabel;
  const scope = dashboard?.scope ?? fallbackScope;
  const summary = dashboard?.summary;
  const barangays = dashboard?.barangays ?? emptyBarangayStatistics;
  const [selectedBarangay, setSelectedBarangay] = useState<DashboardResponse["barangays"][number] | null>(null);
  const barangayContextId = scope.type === "BARANGAY" ? scope.barangayId : null;
  const municipalityMap = scope.type === "MUNICIPALITY_CITY";
  const displayedTotals = resolveDashboardPrimaryTotals(
    summary,
    municipalityMap ? selectedBarangay : null,
  );

  const handleBarangaySelect = useCallback((barangay: DashboardResponse["barangays"][number] | null) => {
    setSelectedBarangay(reconcileDashboardBarangaySelection(barangay, barangays));
  }, [barangays]);

  useEffect(() => {
    if (!municipalityMap) {
      setSelectedBarangay(null);
      return;
    }

    setSelectedBarangay((current) => reconcileDashboardBarangaySelection(current, barangays));
  }, [barangays, municipalityMap]);

  // No active RBIM role is province-scoped. Fail closed in the UI if stale or
  // invalid session data ever supplies a province scope.
  if (scope.type === "PROVINCE") {
    return (
      <div className="dashboard-page municipal-dashboard">
        <div className="dashboard-error" role="alert">
          <span>This account does not have a valid RBIM Barangay or Municipality assignment.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page municipal-dashboard">
      {onRefresh ? (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            aria-label="Refresh dashboard data"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "8px 12px",
              border: "1px solid #d8e1ea",
              borderRadius: 8,
              background: "#ffffff",
              color: "#294766",
              fontWeight: 700,
              cursor: loading ? "default" : "pointer",
              opacity: loading ? 0.65 : 1,
            }}
          >
            <RefreshCw size={15} aria-hidden="true" />
            {loading ? "Refreshing..." : "Refresh data"}
          </button>
        </div>
      ) : null}

      {error ? (
        <div className="dashboard-error" role="alert">
          <span>{error}</span>
          {onRetry ? <button type="button" onClick={onRetry}>Retry</button> : null}
        </div>
      ) : null}

      <section className="dashboard-metrics" aria-label="RBIM summary statistics">
        <DashboardStatCard
          icon={UsersRound}
          label="Total Population"
          value={displayedTotals?.totalPopulation ?? null}
          loading={loading}
        />
        <DashboardStatCard
          icon={Home}
          label="Total Households"
          value={displayedTotals?.totalHouseholds ?? null}
          loading={loading}
        />
        <DashboardStatCard
          icon={ShieldCheck}
          label="Total Families"
          value={displayedTotals?.totalFamilies ?? null}
          loading={loading}
        />
      </section>

      <div
        className="dashboard-workspace"
        style={{ gridTemplateColumns: "minmax(0, 1fr)" }}
      >
        {municipalityMap ? (
          <MunicipalityBarangayMap
            locationLabel={locationLabel}
            barangays={barangays}
            loading={loading}
            onBarangaySelect={handleBarangaySelect}
          />
        ) : null}
        {showRecentActivity ? (
          <RecentActivityTable
            activities={dashboard?.recentActivity ?? emptyRecentActivity}
            loading={loading}
            viewAllPath={activityPath}
          />
        ) : null}
      </div>

      {showLira ? (
        barangayContextId ? (
          <LiraWidget
            contextBarangayId={barangayContextId}
            contextLabel={`Barangay context: ${locationLabel}`}
          />
        ) : (
          <LiraWidget contextBarangay={selectedBarangay} />
        )
      ) : null}
    </div>
  );
}
